import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useState } from "react";

import {
  EmptyQuestProgress,
  EmptyQuestTaskProgress,
} from "~/components/category/[id]/quest/lib";
import { useGraphqlClient } from "~/lib/client/hooks/useGraphqlClient";
import {
  QuestDetailContentQuery,
  Sdk,
  UserQuestProgressQuery,
} from "~/lib/graphql/client";
import { convertMarkdownToHtml } from "~/lib/markdown-helper";

type Quest = QuestDetailContentQuery["quest"];

type Props = {
  quest: Quest;
};

export type { Quest, Props };

function useProgress(quest: Quest): {
  quest: UserQuestProgressQuery["currentUserQuestProgresses"][number];
  tasks: UserQuestProgressQuery["currentUserQuestTaskProgresses"];
} {
  const client = useGraphqlClient();
  const [progress, setProgress] = useState({
    quest: EmptyQuestProgress(quest.id),
    tasks: quest.questTasks.map((task) => EmptyQuestTaskProgress(task.id)),
  });

  useEffect(() => {
    (async () => {
      if (!client) return;

      client
        .userQuestProgress({
          questIds: [quest.id],
          questTaskIds: quest.questTasks.map((task) => task.id),
        })
        .then(
          ({ currentUserQuestProgresses, currentUserQuestTaskProgresses }) =>
            setProgress({
              quest:
                currentUserQuestProgresses[0] || EmptyQuestProgress(quest.id),
              tasks: quest.questTasks.map((task) => {
                const progress = currentUserQuestTaskProgresses.find(
                  (item) => item.questTaskId === task.id
                );
                return progress || EmptyQuestTaskProgress(task.id);
              }),
            })
        )
        .catch(console.error);
    })();
  }, [client, quest.id, quest.questTasks]);

  return progress;
}

// 期間外はタスク非活性
const eventOutProgressPeriod = (quest: Quest) =>
  !(
    dayjs(quest.startAt).isBefore(dayjs()) &&
    dayjs().isBefore(dayjs(quest.endAt))
  );

function useConvertMarkdownToHtml(text: string) {
  const [html, setHtml] = useState("");

  useEffect(() => {
    (async () => {
      setHtml(await convertMarkdownToHtml(text));
    })();
  }, [text]);

  return html;
}

function shareOnTwitter(quest: Quest) {
  const url = `https://www.fussy.fun/quest/${quest.id}`;
  const text = `クエスト：${quest.title} をクリアしました！`;
  const hashtags = quest.category.name;
  const via = "fussy_official";

  const shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    text
  )}&hashtags=${encodeURIComponent(hashtags)}&url=${encodeURIComponent(
    url
  )}&via=${encodeURIComponent(via)}`;
  window.open(shareUrl, "_blank");
}

function useCurrentUserQuestProgress(
  client: Sdk,
  questIds: number[],
  questTaskIds: number[]
) {
  return useQuery({
    queryKey: ["currentUserQuestProgress", questIds, questTaskIds],
    queryFn: async () =>
      await client.currentUserQuestProgress({ questIds, questTaskIds }),
  });
}

export {
  useProgress,
  eventOutProgressPeriod,
  shareOnTwitter,
  useConvertMarkdownToHtml,
  useCurrentUserQuestProgress,
};
